/**
 * Auth-Context + Gate für das bidi-handel Deployment (2026-06-24).
 *
 * Lädt beim App-Start `/api/auth/me` und entscheidet:
 *   - 200 + `authenticated:true` → eingeloggt, App rendert normal.
 *   - 401 → `<LoginPage>` wird angezeigt (statt des Routers).
 *   - "auth-disabled"-Mode → Bypass (Dev-Builds ohne BIDI_AUTH_*-Env).
 *
 * Bewusst KEIN Router-Route für Login: das Login ist eine Vollbild-Seite, die
 * `<AuthGate>` oberhalb des `<RouterProvider>` rendert, solange anonym. Das
 * hält router.tsx unangetastet.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { LoginPage } from "./LoginPage";

type AuthState =
  | { status: "loading" }
  | { status: "authenticated"; username: string; mode?: string }
  | { status: "anonymous" };

interface AuthContextValue {
  state: AuthState;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  login: (
    username: string,
    password: string
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchMe(): Promise<AuthState> {
  try {
    const res = await fetch("/api/auth/me", { credentials: "include" });
    if (!res.ok) return { status: "anonymous" };
    const data = (await res.json()) as {
      authenticated?: boolean;
      username?: string;
      mode?: string;
    };
    if (data.authenticated && data.username) {
      return {
        status: "authenticated",
        username: data.username,
        mode: data.mode,
      };
    }
    return { status: "anonymous" };
  } catch {
    return { status: "anonymous" };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    fetchMe().then(setState);
  }, []);

  const refresh = async () => {
    setState(await fetchMe());
  };

  const logout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* ignore — Cookie wird clientseitig ohnehin verworfen */
    }
    setState({ status: "anonymous" });
  };

  const login = async (
    username: string,
    password: string
  ): Promise<{ ok: true } | { ok: false; error: string }> => {
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        return {
          ok: false,
          error: data.error ?? `Login fehlgeschlagen (${res.status})`,
        };
      }
      await refresh();
      return { ok: true };
    } catch (err) {
      return {
        ok: false,
        error:
          err instanceof Error
            ? err.message
            : "Login fehlgeschlagen (Netzwerk)",
      };
    }
  };

  return (
    <AuthContext.Provider value={{ state, refresh, logout, login }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth muss innerhalb von <AuthProvider> stehen");
  return ctx;
}

/**
 * Gate-Wrapper: zeigt Lade-Indikator während des initialen /me-Checks,
 * die Login-Seite solange anonym, und die App (children) wenn eingeloggt.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  if (state.status === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--bg-void)",
          color: "var(--fg-dim)",
          fontSize: 13,
        }}
      >
        Lade bidi·handel …
      </div>
    );
  }
  if (state.status === "anonymous") {
    return <LoginPage />;
  }
  return <>{children}</>;
}
